import { Badge, Loader, Modal, Tabs } from "@mantine/core";
import { Divider, message, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import smalltalk from "smalltalk";
import { useReactToPrint } from "react-to-print";
import { usePDF } from "react-to-pdf";
import { useQuery, useQueryClient } from "react-query";
import {
  deletePatientMedicalReport,
  getConsultationsByPatient,
  getMoremiMedicalReport,
  getPatientMedicalReportById,
  getPatientMedicalReports,
  postPatientMedicalReport,
  putPatientMedicalReport,
} from "../../helpers/api";
import { QuillEditor } from "../quill-editor";
import { RequiredIndicator } from "../index";
import { format, isValid, parseISO } from "date-fns";
import riviaLogo from "../../static/img/rivia-clinics-logo.png";

const STATUS_OPTIONS = ["draft", "signed", "review", "released"];

const getReportStatusBadgeColor = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "draft":
      return "gray";
    case "signed":
      return "blue";
    case "review":
    case "released":
      return "green";
    default:
      return "gray";
  }
};

const formatReportListDoctor = (r) => {
  const d = r.doctor;
  if (!d) return "—";
  const parts = [d.firstName, d.lastName].filter(Boolean).join(" ").trim();
  if (!parts) return "—";
  return `Dr. ${parts}`;
};

const formatDateLabel = (dateLike) => {
  if (!dateLike) return "—";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const sanitizeDateYMD = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.toLowerCase().includes("invalid")) return "";

    // Already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    // ISO-like string => take first 10 chars if they look like YYYY-MM-DD
    const maybePrefix = trimmed.substring(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(maybePrefix)) return maybePrefix;

    // Fallback: parse & re-format
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return value.toISOString().slice(0, 10);
  }

  // Last resort
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const pdfFilenameForReport = (report) => {
  if (!report) return "medical-report.pdf";
  const ymd = sanitizeDateYMD(report.reportDate || report.createdAt);
  return `medical-report-${ymd || "report"}.pdf`;
};

/** Parse API / date input for display; YYYY-MM-DD uses parseISO to avoid TZ shift. */
const parseReportDateInput = (dateLike) => {
  if (!dateLike) return null;
  const d =
    typeof dateLike === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateLike.trim())
      ? parseISO(dateLike.trim())
      : new Date(dateLike);
  return isValid(d) ? d : null;
};

const patientFullName = (p) => {
  if (!p) return "—";
  return [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || "—";
};

const formatPatientContact = (p) => {
  if (!p?.contact) return "—";
  const cc = p.countryCode
    ? `+${String(p.countryCode).replace(/^\+/, "")} `
    : "";
  return `${cc}${p.contact}`.trim();
};

const doctorDisplayName = (doc) => {
  if (!doc) return "—";
  const n = [doc.firstName, doc.lastName].filter(Boolean).join(" ").trim();
  return n ? `${n}` : "—";
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatReportSectionValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string") return `<p>${escapeHtml(value)}</p>`;
  if (Array.isArray(value)) {
    const items = value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "string") return `<li>${escapeHtml(item)}</li>`;
        if (typeof item === "object") {
          const line = Object.entries(item)
            .filter(([, v]) => v != null && v !== "")
            .map(([k, v]) => `${k}: ${v}`)
            .join(" — ");
          return line ? `<li>${escapeHtml(line)}</li>` : "";
        }
        return "";
      })
      .filter(Boolean)
      .join("");
    return items ? `<ul>${items}</ul>` : "";
  }
  if (typeof value === "object") {
    const lines = Object.entries(value)
      .filter(([, v]) => v != null && v !== "")
      .map(
        ([k, v]) =>
          `<p><strong>${escapeHtml(k)}:</strong> ${escapeHtml(
            typeof v === "object" ? JSON.stringify(v) : v
          )}</p>`
      );
    return lines.join("");
  }
  return `<p>${escapeHtml(String(value))}</p>`;
};

/** Normalize Moremi clinical_summary / legacy report response for Quill (HTML string). */
const normalizeMeromiReportHtml = (data) => {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const pick =
      data.report ?? data.html ?? data.medical_report;
    if (typeof pick === "string") return pick;

    const sections = Object.entries(data)
      .filter(([key]) => key !== "report_metadata")
      .map(([key, value]) => {
        const body = formatReportSectionValue(value);
        if (!body) return "";
        const title = escapeHtml(
          key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        );
        return `<h3>${title}</h3>${body}`;
      })
      .filter(Boolean);

    if (sections.length > 0) return sections.join("");
  }
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
};

const MedicalReports = ({
  patientId,
  patientName,
  onUpdated,
  initialConsultationIdForGeneration,
}) => {
  const { Option } = Select;
  const queryClient = useQueryClient();
  const printAreaRef = useRef(null);
  const { toPDF, targetRef } = usePDF({ method: "save" });
  const handlePrintReport = useReactToPrint({
    content: () => printAreaRef.current,
    documentTitle: `Medical report — ${(patientName || "Patient").trim()}`,
    copyStyles: true,
    pageStyle: `@page { size: A4; margin: 14mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }`,
  });

  const [selectedReport, setSelectedReport] = useState(null);
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [aiGenerateModalOpen, setAiGenerateModalOpen] = useState(false);
  const [meromiReportLoading, setMeromiReportLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    status: "draft",
    report: "",
    reportDate: new Date().toISOString().slice(0, 10),
    releaseDate: "",
    reviewerName: "",
    consultationIds: [],
  });

  const reportsQuery = useQuery({
    queryKey: ["patient-medical-reports", patientId],
    queryFn: () => getPatientMedicalReports(patientId),
    enabled: Boolean(patientId),
    onError: () => message.error("Failed to load medical reports"),
    onSuccess: (data) => {
      const list = data || [];
      setSelectedReport((prev) => {
        if (list.length === 0) return null;
        const still = list.find((r) => r.id === prev?.id);
        return still || list[0];
      });
    },
  });

  const reports = reportsQuery.data ?? [];

  const reportDetailQuery = useQuery({
    queryKey: ["patient-medical-report", patientId, selectedReport?.id],
    queryFn: () =>
      getPatientMedicalReportById(patientId, selectedReport.id),
    enabled: Boolean(patientId && selectedReport?.id),
    onError: () => {
      message.error("Failed to load report details");
    },
  });

  const reportDetail = reportDetailQuery.data ?? null;

  const consultationsQuery = useQuery({
    queryKey: ["patient-consultations", patientId],
    queryFn: () => getConsultationsByPatient(patientId),
    enabled: Boolean(patientId) && aiGenerateModalOpen,
    onError: () => message.error("Failed to load consultations"),
  });

  const consultationsList = Array.isArray(consultationsQuery.data)
    ? consultationsQuery.data
    : [];

  useEffect(() => {
    if (!patientId) setSelectedReport(null);
  }, [patientId]);

  const startNewReport = () => {
    setEditing(true);
    setForm({
      title: "",
      status: "draft",
      report: "",
      reportDate: new Date().toISOString().slice(0, 10),
      releaseDate: "",
      reviewerName: "",
      consultationIds: [],
    });
  };

  const startEditReport = async (report) => {
    setSelectedReport(report);
    try {
      const full = await queryClient.fetchQuery({
        queryKey: ["patient-medical-report", patientId, report.id],
        queryFn: () => getPatientMedicalReportById(patientId, report.id),
      });
      setEditing(true);
      setForm({
        id: full.id,
        title: full.title || "",
        status: full.status || "draft",
        report: full.rawReport || "",
        reportDate:
          sanitizeDateYMD(full.reportDate) ||
          sanitizeDateYMD(full.createdAt) ||
          new Date().toISOString().slice(0, 10),
        releaseDate: sanitizeDateYMD(full.releaseDate),
        reviewerName: full.reviewerName || "",
        consultationIds: full.consultationIds || [],
      });
    } catch {
      message.error("Failed to load report for editing");
    }
  };

  const handleSubmit = async () => {
    const payload = { ...form };
    if (payload.reviewerName != null) {
      const t = String(payload.reviewerName).trim();
      payload.reviewerName = t === "" ? null : t;
    }

    // Ensure date-only fields are valid before sending to backend.
    payload.reportDate =
      sanitizeDateYMD(payload.reportDate) || new Date().toISOString().slice(0, 10);
    payload.releaseDate = sanitizeDateYMD(payload.releaseDate);

    if (payload.status === "released") {
      payload.releaseDate =
        payload.releaseDate || new Date().toISOString().slice(0, 10);
    } else {
      // Avoid sending empty strings to DATEONLY fields (can become "Invalid date")
      payload.releaseDate = payload.releaseDate || null;
    }

    setIsSaving(true);
    try {
      let res = null;
      if (payload.id) {
        res = await putPatientMedicalReport(patientId, payload.id, payload);
      } else {
        res = await postPatientMedicalReport(patientId, payload);
      }

      if (res.status === 201 || res.status === 200) {
        message.success("Medical report saved");
        setEditing(false);
        await queryClient.invalidateQueries({
          queryKey: ["patient-medical-reports", patientId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["patient-medical-report", patientId],
        });
        if (onUpdated) onUpdated();
      } else {
        message.error(res.response?.data?.message || "Failed to save report");
      }
    } catch (error) {
      const err = error?.response?.data?.message;
      if (_.isArray(err)) {
        err.map((e) => message.error(e.message));
      } else {
        message.error(err || "Failed to save report");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (reportId) => {
    try {
      const res = await deletePatientMedicalReport(patientId, reportId);
      if (res.status === 204) {
        message.success("Medical report deleted");
        await queryClient.invalidateQueries({
          queryKey: ["patient-medical-reports", patientId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["patient-medical-report", patientId],
        });
        if (onUpdated) onUpdated();
      } else {
        message.error(res.response?.data?.message || "Failed to delete report");
      }
    } catch (error) {
      message.error("Failed to delete report");
    }
  };

  const requestDeleteReport = (reportId) => {
    smalltalk
      .confirm(
        "Delete medical report",
        "This action cannot be undone. Continue?",
        {
          buttons: {
            ok: "YES",
            cancel: "NO",
          },
        }
      )
      .then(() => {
        handleDelete(reportId);
      })
      .catch(() => { });
  };

  const generateReport = () => {
    if (!patientId) {
      message.warning("Select a visit to generate a report.");
      return;
    }
    setAiGenerateModalOpen(true);
  };

  const runAiReportGeneration = async (consultationId) => {
    setMeromiReportLoading(true);
    try {
      const data = await getMoremiMedicalReport(consultationId);
      const html = normalizeMeromiReportHtml(data);
      setForm((prev) => ({
        ...prev,
        report: html,
        consultationIds: [consultationId],
      }));
      setAiGenerateModalOpen(false);
      setEditing(true);
      message.success("AI draft loaded into the report editor.");
    } catch (err) {
      if (err.message?.includes("API key")) message.error(err.message);
      else {
        message.error(
          err.response?.data?.message ||
          err.message ||
          "AI report generation failed."
        );
      }
    } finally {
      setMeromiReportLoading(false);
    }
  };

  const handleAiConsultationSelect = (consultationId) =>
    runAiReportGeneration(consultationId);

  const initialGenerationStarted = useRef(false);
  useEffect(() => {
    if (
      !initialConsultationIdForGeneration ||
      !patientId ||
      initialGenerationStarted.current
    ) {
      return;
    }
    initialGenerationStarted.current = true;
    setEditing(true);
    setForm({
      title: "",
      status: "draft",
      report: "",
      reportDate: new Date().toISOString().slice(0, 10),
      releaseDate: "",
      reviewerName: "",
      consultationIds: [initialConsultationIdForGeneration],
    });
    runAiReportGeneration(initialConsultationIdForGeneration);
  }, [initialConsultationIdForGeneration, patientId]);

  const listRow =
    reports.find((r) => r.id === selectedReport?.id) || selectedReport;
  const current =
    reportDetail?.id === selectedReport?.id ? reportDetail : null;

  const reportOrCreatedDate = current
    ? parseReportDateInput(current.reportDate || current.createdAt)
    : null;
  const reportDateDdMmYyyy = reportOrCreatedDate
    ? format(reportOrCreatedDate, "dd.MM.yyyy")
    : "—";

  let releaseDateDdMmYyyy = null;
  if (current?.releaseDate) {
    const d = parseReportDateInput(current.releaseDate);
    releaseDateDdMmYyyy = d ? format(d, "dd.MM.yyyy") : "—";
  }

  const patientDobParsed = current?.patient?.dob
    ? parseReportDateInput(current.patient.dob)
    : null;
  const patientDobDdMmYyyy = patientDobParsed
    ? format(patientDobParsed, "dd.MM.yyyy")
    : "—";

  const reportsHeading = `${(patientName || "Patient").trim()} Reports`;

  const detailLoading = reportDetailQuery.isLoading;
  const canDownloadOrPrint = !editing && current && !detailLoading;

  return (
    <section>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <p className="mb-0 h6">{reportsHeading}</p>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {canDownloadOrPrint && (
            <>
              <button
                type="button"
                className="button app-btn"
                onClick={() => {
                  message.info("Preparing PDF…");
                  toPDF({
                    method: "save",
                    filename: pdfFilenameForReport(current),
                  });
                }}>
                <span className="bi bi-file-pdf me-2" aria-hidden />
                Download PDF
              </button>
              <button
                type="button"
                className="button app-btn"
                onClick={handlePrintReport}>
                <span className="bi bi-printer me-2" aria-hidden />
                Print
              </button>
            </>
          )}
          <button
            type="button"
            className="button app-btn"
            onClick={generateReport}>
            <span className="bi bi-stars me-2" />
            AI Generate
          </button>
          <button
            type="button"
            className="button app-btn btn-prim"
            onClick={startNewReport}>
            <span className="bi bi-plus-circle me-2" />
            New Report
          </button>
        </div>
      </div>

      {reportsQuery.isLoading ? (
        <div className="text-center my-5">
          <Loader /> Loading reports...
        </div>
      ) : (
        <div className="row">
          <div className="col-md-4 col-12 border-end" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {reports.length === 0 ? (
              <p>No reports yet.</p>
            ) : (
              <ul
                className="list-unstyled mb-0"
                role="listbox"
                aria-label="Medical reports for this patient">
                {reports.map((r, index) => {
                  const isSelected = selectedReport?.id === r.id;
                  const reportDateLabel = formatDateLabel(
                    r.reportDate || r.createdAt
                  );
                  return (
                    <li
                      key={r.id}
                      className={[
                        "py-2 px-2 medical-reports-list-item border-bottom",
                        isSelected ? "selected-item" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedReport(r)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedReport(r);
                        }
                      }}
                      role="option"
                      tabIndex={0}
                      aria-selected={isSelected}>
                      <div className="row align-items-start g-2">
                        <div className="col-auto text-muted small pt-1">
                          {index + 1}.
                        </div>
                        <div className="col min-w-0">
                          <div className="mb-1">
                            <Badge
                              color={getReportStatusBadgeColor(r.status)}
                              variant="light"
                              className="text-capitalize">
                              {r.status}
                            </Badge>
                          </div>
                          <div className="fw-semibold text-truncate">
                            {reportDateLabel}
                          </div>
                          <div className="small text-muted">
                            <div className="text-truncate">
                              {formatReportListDoctor(r)}
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-auto">
                          <div className="d-flex justify-content-md-end justify-content-start gap-2 align-items-center flex-wrap">
                            <button
                              type="button"
                              className="button app-btn btn-text m-0 px-2"
                              title="Edit report"
                              aria-label="Edit report"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditReport(r);
                              }}>
                              <span className="bi bi-pencil me-2" aria-hidden />
                              Edit
                            </button>

                            <button
                              type="button"
                              className="button app-btn btn-text m-0 px-2 text-danger"
                              title="Delete report"
                              aria-label="Delete report"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestDeleteReport(r.id);
                              }}>
                              <span className="bi bi-trash me-2" aria-hidden />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="col-md-8 col-12">
            {editing ? (
              <div className="position-relative">
                {meromiReportLoading ? (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                    style={{ zIndex: 1, minHeight: 200 }}>
                    <div className="text-center">
                      <Loader />
                      <div className="small text-muted mt-2">
                        Generating report…
                      </div>
                    </div>
                  </div>
                ) : null}
              <>
                <div className="row mb-3">
                  {/* <div className="col-md-6">
                    <div className="field">
                      <label className="mb-0">
                        Title
                      </label>
                      <input
                        className="input"
                        value={form.title}
                        onChange={(e) =>
                          setForm({ ...form, title: e.target.value })
                        }
                        placeholder="optional title"
                      />
                    </div>
                  </div> */}
                  <div className="col">
                    <div className="field">
                      <label className="mb-0">
                        Report Date
                        <RequiredIndicator />
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={form.reportDate || ""}
                        onChange={(e) =>
                          setForm({ ...form, reportDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="col">
                    <div className="field">
                      <label className="mb-0">
                        Release Date
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={form.releaseDate || ""}
                        onChange={(e) =>
                          setForm({ ...form, releaseDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="col">
                    <div className="field">
                      <label className="mb-0">
                        Status
                        <RequiredIndicator />
                      </label>
                      <Select
                        size="large"
                        value={form.status}
                        onChange={(v) => setForm({ ...form, status: v })}
                        className="d-block">
                        {STATUS_OPTIONS.map((s) => (
                          <Option key={s} value={s}>
                            {s}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="col">
                    <div className="field">
                      <label className="mb-0">Reviewer</label>
                      <input
                        type="text"
                        className="input"
                        value={form.reviewerName ?? ""}
                        onChange={(e) =>
                          setForm({ ...form, reviewerName: e.target.value })
                        }
                        placeholder="Reviewer name"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                <div className="field">
                  <Divider />
                  <QuillEditor
                    value={form.report ?? ""}
                    onChange={(html) =>
                      setForm({ ...form, report: html })
                    }
                    placeholder="Write medical report here..."
                  />
                </div>


                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className={`button app-btn btn-prim mt-3${isSaving ? " is-loading" : ""}`}>
                  <span className="bi bi-save me-2" />
                  Save Report
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  disabled={isSaving}
                  className="button h6 app-btn btn-text ms-2 mt-3">
                  Cancel
                </button>
              </>
              </div>
            ) : listRow ? (
              <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {detailLoading ? (
                  <div className="text-center my-5">
                    <Loader /> Loading report…
                  </div>
                ) : current ? (
                  <>
                    <div ref={printAreaRef} className="p-0">
                      <div ref={targetRef}>
                        <article
                          className="bg-white fs-6 lh-base border rounded-2"
                          style={{
                            maxWidth: "210mm",
                            margin: "0 auto",
                            padding: "1.25rem 1.5rem 2rem",
                          }}>
                          <header className="pb-3 border-bottom border-1 my-2">
                            <div className="d-flex w-100 justify-content-between align-items-center gap-3">
                              <div className="flex-shrink-0">
                                <img
                                  src={riviaLogo}
                                  alt="Rivia Clinics logo"
                                  className="mb-0 d-block"
                                  style={{
                                    height: 52,
                                    width: "auto",
                                    objectFit: "contain",
                                  }}
                                />
                              </div>

                              <div className="text-end flex-grow-1 min-w-0">
                                <h2 className="mb-0">
                                  {current.facilityBranch?.facilityName ||
                                    "Rivia Clinics"}
                                </h2>

                                {/* {current.facilityBranch?.location ? (
                                  <p className="small text-muted mb-0">
                                    <strong>Location:</strong> 
                                    {current.facilityBranch.location}
                                  </p>
                                ) : null} */}

                                <p className="small text-muted mb-0">
                                  {current.facilityBranch?.contact ? (
                                    <>
                                      <strong>Phone: </strong>{" "}
                                      {current.facilityBranch.contact}
                                    </>
                                  ) : null}
                                </p>

                                <p className="small text-muted mb-0">
                                  {current.facilityBranch?.email ? (
                                    <>
                                      <strong>Email: </strong>
                                      {current.facilityBranch.email}
                                    </>
                                  ) : null}
                                </p>
                              </div>
                            </div>
                          </header>

                          <h5 className="text-center mt-3 mb-0">
                            MEDICAL REPORT
                          </h5>
                          <p className="small text-muted text-center">
                            <span className="text-capitalize">{current.status}</span>
                            {current.reportDate || current.createdAt ? (
                              <>
                                · Report Date {reportDateDdMmYyyy}
                              </>
                            ) : null}
                            {current.releaseDate ? (
                              <>
                                {" "}
                                · Release Date {releaseDateDdMmYyyy}
                              </>
                            ) : null}
                            {current.reviewerName ? (
                              <> · Reviewer: {current.reviewerName.trim()}</>
                            ) : null}
                          </p>

                          <p className="fw-bold mb-3 border-bottom border-1 pb-1">
                            Report info
                          </p>


                          <div className="row row-cols-1 row-cols-md-2 g-3">
                            <div className="col d-flex flex-wrap align-items-baseline gap-2">
                              <span className="text-nowrap">
                                Physician:
                              </span>
                              <span className="flex-grow-1 text-break">
                                {doctorDisplayName(current.doctor)}
                              </span>
                            </div>
                            <div className="col d-flex flex-wrap align-items-baseline gap-2">
                              <span className="text-nowrap">
                                Visit date:
                              </span>
                              <span className="flex-grow-1 text-break">
                                {reportDateDdMmYyyy}
                              </span>
                            </div>

                          </div>

                          <div className="my-5">
                            <p className="fw-bold mb-3 pb-1 border-bottom border-1">
                              Patient info
                            </p>
                            <div className="row row-cols-1 row-cols-md-2 g-3">
                              <div className="col d-flex flex-wrap align-items-baseline gap-2">
                                <span className="text-nowrap">
                                  Full Name:
                                </span>
                                <span className="flex-grow-1 text-break">
                                  {patientFullName(current.patient)}
                                </span>
                              </div>
                              <div className="col d-flex flex-wrap align-items-baseline gap-2">
                                <span className="text-nowrap">
                                  Birth Date:
                                </span>
                                <span className="flex-grow-1 text-break">
                                  {patientDobDdMmYyyy}
                                </span>
                              </div>
                              <div className="col d-flex flex-wrap align-items-baseline gap-2">
                                <span className="text-nowrap">
                                  Gender:
                                </span>
                                <span className="flex-grow-1 text-break">
                                  {current.patient?.gender || "—"}
                                </span>
                              </div>
                              <div className="col d-flex flex-wrap align-items-baseline gap-2">
                                <span className="text-nowrap">
                                  Phone:
                                </span>
                                <span className="flex-grow-1 text-break">
                                  {formatPatientContact(current.patient)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="fw-bold mt-4 mb-3 pb-1 border-bottom border-1">
                            Clinical report
                          </p>
                          {current.rawReport ? (
                            <div
                              className="mt-3"
                              role="document"
                              aria-label="Clinical report content"
                              dangerouslySetInnerHTML={{
                                __html: current.rawReport,
                              }}
                            />
                          ) : (
                            <p className="text-muted">No content</p>
                          )}

                          {current.doctor && (
                            <div className="mt-4 pt-3 border-top border-1">
                              <em className="mb-1">
                                {doctorDisplayName(current.doctor)}
                              </em>
                              <div className="d-flex align-items-center flex-wrap gap-2">
                                {current.doctor.signatureUrl && (
                                  <img
                                    src={current.doctor.signatureUrl}
                                    alt="Signature"
                                    style={{
                                      maxHeight: 90,
                                      maxWidth: 160,
                                      objectFit: "contain",
                                    }}
                                  />
                                )}
                                {current.doctor.stampUrl && (
                                  <img
                                    src={current.doctor.stampUrl}
                                    alt="Stamp"
                                    style={{
                                      maxHeight: 90,
                                      maxWidth: 160,
                                      objectFit: "contain",
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </article>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-muted">Could not load this report.</p>
                )}
              </div>
            ) : (
              <p>Select a report to view</p>
            )}
          </div>
        </div>
      )}

      <Modal
        opened={aiGenerateModalOpen}
        onClose={() => {
          if (!meromiReportLoading) setAiGenerateModalOpen(false);
        }}
        title="Select a visit for AI report"
        size="lg"
        centered>
        {consultationsQuery.isLoading ? (
          <div className="text-center py-4">
            <Loader /> Loading visits…
          </div>
        ) : consultationsQuery.isError ? (
          <p className="text-danger mb-0">Could not load consultations.</p>
        ) : consultationsList.length === 0 ? (
          <p className="text-muted mb-0">No consultations found for this patient.</p>
        ) : (
          <div className="table-responsive position-relative">
            {meromiReportLoading ? (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                style={{ zIndex: 1 }}>
                <div className="text-center">
                  <Loader />
                  <div className="small text-muted mt-2">Generating report…</div>
                </div>
              </div>
            ) : null}
            <table className="table table-sm table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">Visit Date</th>
                  <th scope="col">Code</th>
                  <th scope="col">Type</th>
                  <th scope="col" className="text-end">

                  </th>
                </tr>
              </thead>
              <tbody>
                {consultationsList.map((c) => (
                  <tr key={c.id}>
                    <td>{formatDateLabel(c.createdAt)}</td>
                    <td>
                      <strong>{c.code || "—"}</strong>
                    </td>
                    <td className="text-capitalize">{c.type || "—"}</td>
                    <td className="text-end">
                      <button
                        className="app-btn btn-text btn-sm"
                        disabled={meromiReportLoading}
                        onClick={() => handleAiConsultationSelect(c.id)}>
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default MedicalReports;

